import { DocumentUploadRejectedWarning } from '@monorepo/shared/componentsV2/documents/DocumentUploadRejectedWarning';
import { showDocumentUploadToast } from '@monorepo/shared/componentsV2/documents/documentUploadToast';
import { FileInput } from '@monorepo/shared/componentsV2/FileInput';
import { UserFriendlyErrorMessage } from '@monorepo/shared/componentsV2/errorDisplays/UserFriendlyErrorMessage';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import {
  DOCUMENT_FILE_INPUT_ACCEPT,
  DocumentBulkUploadResult,
  partitionDocumentUploadFiles,
} from '@monorepo/shared/utils/documentUpload';
import React, { useCallback } from 'react';

type DocumentUploadFileInputProps = {
  inputId: string;
  uploadButtonText: string;
  isUploading?: boolean;
  onUpload: (files: File[]) => Promise<DocumentBulkUploadResult | undefined>;
};

export function DocumentUploadFileInput({
  inputId,
  uploadButtonText,
  isUploading = false,
  onUpload,
}: DocumentUploadFileInputProps) {
  const { showUserFriendlyErrorToast, error, success, warning } = useToast();

  const handleFiles = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (!files.length) return;

      const { accepted, rejectedMessages } =
        partitionDocumentUploadFiles(files);

      if (!accepted.length) {
        if (rejectedMessages.length) {
          error(<DocumentUploadRejectedWarning rejected={rejectedMessages} />);
        }
        return;
      }

      try {
        const result = await onUpload(accepted);
        // react-query v2 typings allow the result to be undefined; in practice the
        // mutation throws on failure when configured with `throwOnError: true`,
        // but guard defensively so TS is happy and we never crash on missing data.
        if (!result) return;
        showDocumentUploadToast(rejectedMessages, result, {
          error,
          success,
          warning,
        });
      } catch (err) {
        if (rejectedMessages.length) {
          error(
            <>
              <DocumentUploadRejectedWarning rejected={rejectedMessages} />
              <br />
              <UserFriendlyErrorMessage
                error={err}
                prependedText="Unable to upload documents."
              />
            </>,
          );
        } else {
          showUserFriendlyErrorToast(err, 'Unable to upload documents.');
        }
      }
    },
    [error, onUpload, showUserFriendlyErrorToast, success, warning],
  );

  return (
    <FileInput
      multiple
      accept={DOCUMENT_FILE_INPUT_ACCEPT}
      handleFileChange={handleFiles}
      buttonText={isUploading ? 'Uploading…' : uploadButtonText}
      inputId={inputId}
    />
  );
}
